/**
 * LoanExtractsViewer — the 3-pane extracted-document viewer
 * (frontend_documentation/document-extracts-viewer.md), opened as a full-screen
 * dialog from the Documents tab.
 *
 * Left rail: one entry per parsed source document. Main area for the selected doc:
 *   • Source pane  — the original file inline (PDF/img).
 *   • Markdown pane — OCR markdown, rendered (react-markdown) with a View⇄Edit
 *     toggle and an Original|Edited switch. Edits are saved as sibling blobs; the
 *     AI original is never overwritten.
 *   • Structured pane — the structured JSON (editable, validated) + an image gallery.
 *
 * Artifacts are addressed by `blob_name` (their identity in `{dealId}-extracted`),
 * fetched lazily via losExtractsApi. Object URLs are revoked on teardown.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";

import { losExtractsApi } from "../../../../../api/losExtractsApi";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import CreateConditionFromDocButton from "../../../../../components/common/Loan/buttons/CreateConditionFromDocButton";
import JsonPreview from "../../../../../components/common/JsonPreview";

export const T = {
  bg: "#f7f9fa",
  line: "#e3e9ec",
  ink: "#0f172a",
  sub: "#64748b",
  blue: "#0a66ff",
};

// The OCR markdown from Azure Document Intelligence embeds raw HTML tables
// (with colspan/rowspan). react-markdown escapes raw HTML by default, so we run
// rehype-raw to render it — paired with rehype-sanitize so a crafted document
// can't inject scripts. The default sanitize schema strips td/th span attrs, so
// re-allow them (hast uses camelCase colSpan/rowSpan) to keep tables intact.
const MD_SANITIZE_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    td: [...(defaultSchema.attributes?.td || []), "colSpan", "rowSpan"],
    th: [...(defaultSchema.attributes?.th || []), "colSpan", "rowSpan"],
  },
};
const MD_REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, MD_SANITIZE_SCHEMA]];
const MD_REMARK_PLUGINS = [remarkGfm];

// Search highlighting for the markdown pane. Implemented as a rehype plugin so
// the <mark> nodes are produced INSIDE the markdown pipeline and rendered by
// React — hand-mutating the rendered DOM would fight React's reconciler. It
// runs LAST (after rehype-sanitize) so the marks it adds aren't stripped.
// `activeIndex` tags the currently-focused hit so it can be styled + scrolled to.
const rehypeHighlightSearch = (query, activeIndex) => () => (tree) => {
  const needle = (query || "").trim().toLowerCase();
  if (!needle) return;
  let hit = 0;
  const walk = (node) => {
    if (!node?.children?.length) return;
    const next = [];
    for (const child of node.children) {
      // Don't highlight inside code blocks — keeps them byte-accurate.
      if (child.type === "element" && (child.tagName === "code" || child.tagName === "pre")) {
        next.push(child);
        continue;
      }
      if (child.type !== "text" || !child.value) {
        walk(child);
        next.push(child);
        continue;
      }
      const value = child.value;
      const lower = value.toLowerCase();
      let idx = lower.indexOf(needle);
      if (idx === -1) {
        next.push(child);
        continue;
      }
      let last = 0;
      while (idx !== -1) {
        if (idx > last) next.push({ type: "text", value: value.slice(last, idx) });
        next.push({
          type: "element",
          tagName: "mark",
          properties: {
            "data-md-hit": String(hit),
            ...(hit === activeIndex ? { "data-md-active": "true" } : {}),
          },
          children: [{ type: "text", value: value.slice(idx, idx + needle.length) }],
        });
        hit += 1;
        last = idx + needle.length;
        idx = lower.indexOf(needle, last);
      }
      if (last < value.length) next.push({ type: "text", value: value.slice(last) });
    }
    node.children = next;
  };
  walk(tree);
};

export const statusColor = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("approv") || v.includes("accept")) return "#16a34a";
  if (v.includes("reject") || v.includes("denied")) return "#dc2626";
  if (v.includes("pending") || v.includes("review")) return "#b45309";
  return "#64748b";
};

// ── Left rail: parsed-document list ──────────────────────────────────────────
function DocRail({ documents, selectedId, onSelect }) {
  return (
    <Box
      sx={{
        width: 280,
        flex: "0 0 auto",
        borderRight: `1px solid ${T.line}`,
        overflowY: "auto",
        background: "#fff",
      }}
    >
      {documents.map((d) => {
        const p = d.parent || d.parent_provenance || {};
        const id = d.parent_document_id;
        const active = id === selectedId;
        const name = p.file_display_name || p.name || "Untitled document";
        const counts = d.counts || {};
        return (
          <Box
            key={id}
            onClick={() => onSelect(id)}
            sx={{
              px: 1.5,
              py: 1.25,
              borderBottom: `1px solid ${T.line}`,
              cursor: "pointer",
              borderLeft: `3px solid ${active ? T.blue : "transparent"}`,
              background: active ? "#f1f7ff" : "transparent",
              "&:hover": { background: active ? "#f1f7ff" : "#f8fafc" },
            }}
          >
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: T.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              {p.docType && (
                <Chip
                  size="small"
                  label={p.docType}
                  sx={{ height: 18, fontSize: 10, bgcolor: "#eef2ff", color: "#4338ca" }}
                />
              )}
              {p.documentStatus && (
                <Chip
                  size="small"
                  label={p.documentStatus}
                  sx={{
                    height: 18,
                    fontSize: 10,
                    bgcolor: `${statusColor(p.documentStatus)}14`,
                    color: statusColor(p.documentStatus),
                  }}
                />
              )}
            </Box>
            <Box sx={{ fontSize: 10.5, color: T.sub, mt: 0.5 }}>
              {counts.markdown || 0} md · {counts.structured_json || 0} json ·{" "}
              {counts.image || 0} img
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Source pane (left): original file inline ─────────────────────────────────
export function SourcePane({ dealId, parent }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const fsn = parent?.file_storage_name;
  const isImage = (parent?.content_type || "").startsWith("image/");

  useEffect(() => {
    if (!dealId || !fsn) return undefined;
    let active = true;
    let made = null;
    setUrl(null);
    setErr(false);
    losExtractsApi
      .getSourceBlobUrl(dealId, fsn)
      .then((u) => {
        if (active) {
          made = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => active && setErr(true));
    return () => {
      active = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [dealId, fsn]);

  if (!fsn)
    return <PaneEmpty label="No source file on record for this extract." />;
  if (err) return <PaneEmpty label="Couldn't load the source file." />;
  if (!url) return <PaneLoading />;
  return isImage ? (
    <Box sx={{ height: "100%", overflow: "auto", textAlign: "center", p: 1, background: "#f1f5f9" }}>
      <Box component="img" src={url} alt={parent.file_display_name} sx={{ maxWidth: "100%" }} />
    </Box>
  ) : (
    <Box component="iframe" src={url} title="source" sx={{ width: "100%", height: "100%", border: "none" }} />
  );
}

// ── Editable text pane (markdown OR structured json) ─────────────────────────
// `collapsible`/`collapsed`/`onToggleCollapse` turn the pane's toolbar into a
// VS-Code-style section header: a chevron toggles the body, and while collapsed
// only the header bar shows (so a sibling pane can claim the freed space).
export function ExtractEditablePane({
  dealId,
  extract,
  kind,
  collapsible = false,
  collapsed = false,
  onToggleCollapse,
  // { query, nonce } — the Structured tab asking to find a value in the text.
  searchRequest,
}) {
  const blobName = extract.blob_name;
  const [text, setText] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("view"); // view | edit
  const [showOriginal, setShowOriginal] = useState(false);
  const [edited, setEdited] = useState(!!extract.edited);
  const [notes, setNotes] = useState(extract.notes || "");
  const [editMeta, setEditMeta] = useState({
    editedBy: extract.editedBy,
    editedAt: extract.editedAt,
  });
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  // ── Markdown search (find-in-pane) ─────────────────────────────────────────
  const bodyRef = useRef(null);
  const [search, setSearch] = useState("");
  const [activeHit, setActiveHit] = useState(0);
  const [hitCount, setHitCount] = useState(0);
  const searchable = kind === "markdown" && mode === "view";

  // Append the highlighter after the sanitizer so its <mark>s survive.
  const rehypePlugins = useMemo(() => {
    const q = search.trim();
    if (!searchable || !q) return MD_REHYPE_PLUGINS;
    return [...MD_REHYPE_PLUGINS, rehypeHighlightSearch(q, activeHit)];
  }, [searchable, search, activeHit]);

  // Count the hits React actually rendered (read-only DOM query — safe).
  useEffect(() => {
    const root = bodyRef.current;
    if (!root || !searchable || !search.trim()) {
      setHitCount(0);
      return;
    }
    setHitCount(root.querySelectorAll("mark[data-md-hit]").length);
  }, [search, text, searchable, showOriginal]);

  // An outside find request (Structured tab → "Show in text").
  useEffect(() => {
    if (!searchRequest?.query) return;
    setMode("view");
    setSearch(searchRequest.query);
  }, [searchRequest]);

  // A new query restarts navigation at the first hit.
  useEffect(() => {
    setActiveHit(0);
  }, [search]);

  // Bring the focused hit into view.
  useEffect(() => {
    const el = bodyRef.current?.querySelector('mark[data-md-active="true"]');
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeHit, hitCount]);

  const gotoHit = (delta) => {
    if (!hitCount) return;
    setActiveHit((a) => (a + delta + hitCount) % hitCount);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    losExtractsApi
      .getText(dealId, blobName, { original: showOriginal })
      .then((t) => {
        if (!active) return;
        const str = typeof t === "string" ? t : JSON.stringify(t, null, 2);
        const pretty =
          kind === "json"
            ? (() => {
                try {
                  return JSON.stringify(JSON.parse(str), null, 2);
                } catch {
                  return str;
                }
              })()
            : str;
        setText(pretty);
        setDraft(pretty);
      })
      .catch(() => active && setText(""))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [dealId, blobName, kind, showOriginal]);

  const handleSave = async () => {
    setJsonError(null);
    let content = draft;
    if (kind === "json") {
      try {
        content = JSON.parse(draft); // validate + send as object
      } catch (e) {
        setJsonError(e.message);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await losExtractsApi.saveEdit(dealId, blobName, {
        content,
        notes,
      });
      setText(draft);
      setEdited(true);
      setEditMeta({ editedBy: res?.editedBy, editedAt: res?.editedAt });
      setMode("view");
      setShowOriginal(false);
    } finally {
      setSaving(false);
    }
  };

  // For JSON view mode we render the interactive JsonPreview tree. Parse the
  // current text into an object; if it isn't valid JSON (e.g. an unparsable
  // edit), `undefined` signals the renderer to fall back to raw <pre> text.
  const parsedJson = useMemo(() => {
    if (kind !== "json") return undefined;
    try {
      return JSON.parse(text);
    } catch {
      return undefined;
    }
  }, [kind, text]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Pane toolbar */}
      <Box
        onClick={collapsible ? () => onToggleCollapse?.() : undefined}
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: collapsed ? "none" : `1px solid ${T.line}`,
          background: "#fbfdfe",
          cursor: collapsible ? "pointer" : "default",
          userSelect: "none",
          "&:hover": collapsible ? { background: "#f1f5f9" } : undefined,
        }}
      >
        {collapsible &&
          (collapsed ? (
            <KeyboardArrowRightRounded sx={{ fontSize: 18, color: T.sub, ml: -0.5 }} />
          ) : (
            <KeyboardArrowDownRounded sx={{ fontSize: 18, color: T.sub, ml: -0.5 }} />
          ))}
        <Box sx={{ fontSize: 12, fontWeight: 700, color: T.sub, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {kind === "json" ? extract.extraction_method || "structured json" : "markdown"}
        </Box>
        {edited && (
          <Tooltip
            title={
              editMeta.editedAt
                ? `Edited by ${editMeta.editedBy || "someone"} · ${formatDateTime(editMeta.editedAt)}`
                : "Has a saved edit"
            }
          >
            <Chip size="small" label="Edited" sx={{ height: 18, fontSize: 10, bgcolor: "#fef3c7", color: "#b45309" }} />
          </Tooltip>
        )}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            ml: "auto",
            display: collapsed ? "none" : "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* Find-in-markdown — highlights hits and steps through them. */}
          {searchable && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                border: `1px solid ${T.line}`,
                borderRadius: "999px",
                pl: 0.75,
                pr: 0.25,
                py: "1px",
                background: "#fff",
              }}
            >
              <SearchRounded sx={{ fontSize: 14, color: T.sub }} />
              <Box
                component="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    gotoHit(e.shiftKey ? -1 : 1);
                  } else if (e.key === "Escape") {
                    setSearch("");
                  }
                }}
                placeholder="Find…"
                spellCheck={false}
                sx={{
                  width: 96,
                  border: "none",
                  outline: "none",
                  fontSize: 11.5,
                  py: 0.25,
                  background: "transparent",
                  color: T.ink,
                }}
              />
              {search.trim() && (
                <>
                  <Box
                    sx={{
                      fontSize: 10.5,
                      color: hitCount ? T.sub : "#b91c1c",
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      px: 0.25,
                    }}
                  >
                    {hitCount ? `${activeHit + 1}/${hitCount}` : "0"}
                  </Box>
                  <Tooltip title="Previous (Shift+Enter)">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!hitCount}
                        onClick={() => gotoHit(-1)}
                        sx={{ p: 0.25 }}
                      >
                        <KeyboardArrowUpRounded sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Next (Enter)">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!hitCount}
                        onClick={() => gotoHit(1)}
                        sx={{ p: 0.25 }}
                      >
                        <KeyboardArrowDownRounded sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Clear (Esc)">
                    <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.25 }}>
                      <CloseRounded sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          )}
          {edited && mode === "view" && (
            <Button size="small" variant="text" onClick={() => setShowOriginal((v) => !v)} sx={{ fontSize: 11, py: 0 }}>
              {showOriginal ? "View edited" : "View original"}
            </Button>
          )}
          {mode === "view" ? (
            <Button size="small" variant="outlined" onClick={() => setMode("edit")} sx={{ fontSize: 11, py: 0.25 }}>
              Edit
            </Button>
          ) : (
            <>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setDraft(text);
                  setMode("view");
                  setJsonError(null);
                }}
                sx={{ fontSize: 11, py: 0.25 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ fontSize: 11, py: 0.25 }}
              >
                {saving ? <CircularProgress size={13} color="inherit" /> : "Save"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Pane body — hidden while the section is collapsed. */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          minHeight: 0,
          display: collapsed ? "none" : "block",
        }}
      >
        {loading ? (
          <PaneLoading />
        ) : mode === "edit" ? (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
              component="textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              sx={{
                flexGrow: 1,
                width: "100%",
                resize: "none",
                border: "none",
                outline: "none",
                p: 1.5,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: 12.5,
                lineHeight: 1.5,
                color: T.ink,
              }}
            />
            {jsonError && (
              <Box sx={{ px: 1.5, py: 0.5, fontSize: 11.5, color: "#b00000", background: "#fff1f1" }}>
                Invalid JSON: {jsonError}
              </Box>
            )}
            <Box sx={{ p: 1.5, borderTop: `1px solid ${T.line}` }}>
              <Box
                component="input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional) — saved alongside the edit"
                sx={{
                  width: "100%",
                  border: `1px solid ${T.line}`,
                  borderRadius: "8px",
                  px: 1,
                  py: 0.75,
                  fontSize: 12.5,
                  outline: "none",
                }}
              />
            </Box>
          </Box>
        ) : kind === "markdown" ? (
          <Box
            className="markdown-body"
            ref={bodyRef}
            sx={{
              // Search hits (injected by rehypeHighlightSearch). The focused
              // hit gets the stronger amber so it stands out while stepping.
              "& mark[data-md-hit]": {
                background: "#fde68a",
                color: "inherit",
                padding: 0,
                borderRadius: "2px",
              },
              "& mark[data-md-active='true']": {
                background: "#fb923c",
                boxShadow: "0 0 0 1px #ea580c",
              },
              p: 2,
              fontSize: 13.5,
              lineHeight: 1.6,
              wordBreak: "break-word",
              "& h1": { fontSize: 20, mt: 1.5, mb: 1 },
              "& h2": { fontSize: 17, mt: 1.5, mb: 0.75 },
              "& h3": { fontSize: 15, mt: 1.25, mb: 0.5 },
              "& p": { my: 0.75 },
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                my: 1.5,
                fontSize: 12,
                tableLayout: "fixed",
              },
              "& td, & th": {
                border: `1px solid ${T.line}`,
                px: 1,
                py: 0.5,
                textAlign: "left",
                verticalAlign: "top",
                wordBreak: "break-word",
              },
              "& th": { background: "#f1f5f9", fontWeight: 700 },
              "& img": { maxWidth: "100%" },
              "& pre": {
                background: "#f1f5f9",
                p: 1,
                borderRadius: 1,
                overflow: "auto",
              },
              "& code": { fontSize: 12 },
            }}
          >
            <ReactMarkdown
              remarkPlugins={MD_REMARK_PLUGINS}
              rehypePlugins={rehypePlugins}
            >
              {text}
            </ReactMarkdown>
          </Box>
        ) : parsedJson !== undefined ? (
          <Box sx={{ p: 1.5 }}>
            <JsonPreview values={parsedJson} defaultCollapse={3} />
          </Box>
        ) : (
          <Box component="pre" sx={{ m: 0, p: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", color: T.ink }}>
            {text}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Image gallery ────────────────────────────────────────────────────────────
export function ImageThumb({ dealId, img }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let made = null;
    losExtractsApi
      .getBlobUrl(dealId, img.blob_name)
      .then((u) => {
        if (active) {
          made = u;
          setUrl(u);
        } else URL.revokeObjectURL(u);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [dealId, img.blob_name]);
  return (
    <Box sx={{ border: `1px solid ${T.line}`, borderRadius: "8px", overflow: "hidden", background: "#fff" }}>
      <Box sx={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9" }}>
        {url ? (
          <Box component="img" src={url} alt={img.image_context || ""} sx={{ maxWidth: "100%", maxHeight: "100%" }} />
        ) : (
          <CircularProgress size={16} />
        )}
      </Box>
      {(img.image_context || img.image_category) && (
        <Box sx={{ px: 1, py: 0.5, fontSize: 10.5, color: T.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={img.image_context}>
          {img.image_category ? `[${img.image_category}] ` : ""}
          {img.image_context}
        </Box>
      )}
    </Box>
  );
}

const PaneLoading = () => (
  <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: T.sub, gap: 1 }}>
    <CircularProgress size={18} />
  </Box>
);
const PaneEmpty = ({ label }) => (
  <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: T.sub, fontSize: 13, p: 2, textAlign: "center" }}>
    {label}
  </Box>
);

const PaneHeader = ({ children }) => (
  <Box sx={{ flex: "0 0 auto", px: 1.5, py: 0.75, fontSize: 12, fontWeight: 700, color: T.sub, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: `1px solid ${T.line}`, background: "#fbfdfe" }}>
    {children}
  </Box>
);

// ── Tri-panel (Source · Markdown · Structured JSON + images) ─────────────────
// The 3-pane view for a single selected extracted document. Extracted from the
// dialog so it can be reused standalone (e.g. the Documents tab's "Extracted
// Documents" table drill-in). Expects a flex/grid parent that gives it height
// (it fills with flexGrow + pins its grid row to the container so panes scroll
// internally).
export function ExtractTriPanel({ dealId, selected }) {
  const parent = selected?.parent || selected?.parent_provenance || {};
  const extracts = selected?.extracts || {};
  const markdowns = extracts.markdown || [];
  const jsons = extracts.structured_json || [];
  const images = extracts.image || [];

  // VS-Code-style collapse for the two vertically stacked sections in the third
  // column. A collapsed section shrinks to just its header bar (flex 0 0 auto)
  // so the expanded sibling fills the freed vertical space.
  const [jsonCollapsed, setJsonCollapsed] = useState(false);
  // Images default to COLLAPSED when the document has none — an empty gallery
  // would otherwise eat half the column to say "No extracted images". Re-derived
  // when switching documents (the user's manual toggle survives within a doc).
  const [imagesCollapsed, setImagesCollapsed] = useState(images.length === 0);
  const selectedKey = selected?.parent_document_id;
  useEffect(() => {
    setImagesCollapsed(images.length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gridTemplateRows: "minmax(0, 1fr)",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {/* Source */}
      <Box sx={{ display: "flex", flexDirection: "column", borderRight: `1px solid ${T.line}`, minWidth: 0, minHeight: 0, overflow: "hidden" }}>
        <PaneHeader>Source — {parent.file_display_name || parent.name || ""}</PaneHeader>
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <SourcePane dealId={dealId} parent={parent} />
        </Box>
      </Box>

      {/* Markdown */}
      <Box sx={{ display: "flex", flexDirection: "column", borderRight: `1px solid ${T.line}`, minWidth: 0, minHeight: 0, overflow: "hidden", background: "#fff" }}>
        {markdowns.length ? (
          <ExtractEditablePane key={markdowns[0].blob_name} dealId={dealId} extract={markdowns[0]} kind="markdown" />
        ) : (
          <>
            <PaneHeader>Markdown</PaneHeader>
            <PaneEmpty label="No OCR markdown for this document." />
          </>
        )}
      </Box>

      {/* Structured JSON + images — two collapsible sections. Each expanded
          section shares the vertical space; collapsing one drops it to a header
          bar so the other fills (VS-Code sidebar behavior). */}
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, overflow: "hidden", background: "#fff" }}>
        <Box
          sx={{
            flex: jsonCollapsed ? "0 0 auto" : "1.2 1 0",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderBottom: `1px solid ${T.line}`,
          }}
        >
          {jsons.length ? (
            <ExtractEditablePane
              key={jsons[0].blob_name}
              dealId={dealId}
              extract={jsons[0]}
              kind="json"
              collapsible
              collapsed={jsonCollapsed}
              onToggleCollapse={() => setJsonCollapsed((v) => !v)}
            />
          ) : (
            <>
              <PaneHeader>Structured JSON</PaneHeader>
              <PaneEmpty label="No structured JSON for this document." />
            </>
          )}
        </Box>
        <Box
          sx={{
            flex: imagesCollapsed ? "0 0 auto" : "1 1 0",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Collapsible section header (VS-Code style). */}
          <Box
            onClick={() => setImagesCollapsed((v) => !v)}
            sx={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.75,
              fontSize: 12,
              fontWeight: 700,
              color: T.sub,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              borderBottom: imagesCollapsed ? "none" : `1px solid ${T.line}`,
              background: "#fbfdfe",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { background: "#f1f5f9" },
            }}
          >
            {imagesCollapsed ? (
              <KeyboardArrowRightRounded sx={{ fontSize: 18, color: T.sub, ml: -0.5 }} />
            ) : (
              <KeyboardArrowDownRounded sx={{ fontSize: 18, color: T.sub, ml: -0.5 }} />
            )}
            Images ({images.length})
          </Box>
          {!imagesCollapsed && (
            <Box sx={{ flexGrow: 1, overflow: "auto", p: 1.5, minHeight: 0 }}>
              {images.length ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 1 }}>
                  {images.map((img) => (
                    <ImageThumb key={img.blob_name} dealId={dealId} img={img} />
                  ))}
                </Box>
              ) : (
                <Box sx={{ fontSize: 12.5, color: T.sub }}>No extracted images.</Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function LoanExtractsViewer({ open, onClose, dealId }) {
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const reqRef = useRef(0);

  const loadIndex = useMemo(
    () => () => {
      const req = ++reqRef.current;
      setLoading(true);
      setError(null);
      losExtractsApi
        .getIndex(dealId)
        .then((data) => {
          if (req !== reqRef.current) return;
          setIndex(data);
          setSelectedId(
            (prev) => prev || data?.documents?.[0]?.parent_document_id || null,
          );
        })
        .catch((e) => req === reqRef.current && setError(e))
        .finally(() => req === reqRef.current && setLoading(false));
    },
    [dealId],
  );

  useEffect(() => {
    if (!open || !dealId) return;
    setIndex(null);
    setSelectedId(null);
    loadIndex();
  }, [open, dealId, loadIndex]);

  const documents = index?.documents || [];
  const selected = useMemo(
    () => documents.find((d) => d.parent_document_id === selectedId),
    [documents, selectedId],
  );
  const parent = selected?.parent || selected?.parent_provenance || {};

  // Resolve the underlying loan document so a condition created from here links
  // to the real LOSLoanDocument (match by storage name, fall back to the id).
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments,
  );
  const resolvedDoc = useMemo(() => {
    if (!selected) return null;
    return (
      loanDocuments?.find(
        (d) =>
          (parent.file_storage_name &&
            d.file_storage_name === parent.file_storage_name) ||
          d._id === selectedId,
      ) || null
    );
  }, [loanDocuments, parent.file_storage_name, selectedId, selected]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", background: T.bg }}>
        {/* Title bar */}
        <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, background: "#fff", borderBottom: `1px solid ${T.line}` }}>
          <DescriptionOutlined sx={{ fontSize: 20, color: T.sub }} />
          <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>Document Extracts</Box>
          <Box sx={{ fontSize: 12, color: T.sub }}>
            OCR markdown · structured JSON · extracted images
          </Box>
          {resolvedDoc && (
            <Box sx={{ ml: "auto" }}>
              {/* Quick-create stays in the viewer; "More options" closes it so
                  the full condition sidebar is visible. */}
              <CreateConditionFromDocButton
                doc={resolvedDoc}
                variant="button"
                onBeforeFullForm={onClose}
              />
            </Box>
          )}
          <Tooltip title="Refresh">
            <span style={{ marginLeft: resolvedDoc ? 0 : "auto" }}>
              <IconButton onClick={loadIndex} disabled={loading} aria-label="Refresh">
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseRounded />
          </IconButton>
        </Box>

        {loading ? (
          <PaneLoading />
        ) : error ? (
          <PaneEmpty label="Couldn't load extracts for this loan." />
        ) : !documents.length ? (
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, color: T.sub, px: 3 }}>
            <DescriptionOutlined sx={{ fontSize: 40, opacity: 0.35 }} />
            <Box sx={{ fontSize: 14, color: T.ink, fontWeight: 600 }}>
              No extracted documents for this loan.
            </Box>
            <Box sx={{ fontSize: 12.5, maxWidth: 520, textAlign: "center", lineHeight: 1.6 }}>
              Extracted artifacts (OCR markdown, structured JSON, images) are produced
              by the Phase-3 extractor as part of the <b>Box&nbsp;→&nbsp;LOS document
              sync</b>, and written to the loan&apos;s <code>-extracted</code> container.
              There is no on-demand trigger today, so this stays empty until that sync
              runs for the loan. (The separate llama-parse / Ocrolus pipeline shows under
              the <b>Parsed&nbsp;Docs</b> panel on the Files tab, not here.)
            </Box>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, display: "flex", minHeight: 0 }}>
            <DocRail documents={documents} selectedId={selectedId} onSelect={setSelectedId} />
            <ExtractTriPanel dealId={dealId} selected={selected} />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
